/**
 * UTM Parameter Capture and Storage
 *
 * Captures UTM parameters from URL on page load and stores them in sessionStorage
 * for attribution when user signs up.
 */

export interface UTMParams {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
  gclid: string | null;        // Google Ads Click ID
  gbraid: string | null;       // Google Ads iOS click ID
  wbraid: string | null;       // Google Ads web-to-app click ID
  msclkid: string | null;      // Microsoft/Bing Ads click ID
  fbclid: string | null;       // Facebook/Meta click ID
  referrer: string | null;
  landing_page: string | null;
  captured_at: string;
}

const UTM_STORAGE_KEY = 'settlewise_utm_params';

/**
 * Capture UTM parameters from current URL and store in sessionStorage.
 * Call this on app initialization (main.tsx).
 *
 * Only captures if UTM params are present - doesn't overwrite existing capture.
 */
export function captureUTMParams(): void {
  try {
    // Don't capture in SSR
    if (typeof window === 'undefined') return;

    const params = new URLSearchParams(window.location.search);

    const utm: UTMParams = {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_content: params.get('utm_content'),
      utm_term: params.get('utm_term'),
      gclid: params.get('gclid'),      // Google Ads click ID
      gbraid: params.get('gbraid'),    // Google Ads iOS click ID
      wbraid: params.get('wbraid'),    // Google Ads web-to-app click ID
      msclkid: params.get('msclkid'),  // Microsoft/Bing Ads click ID
      fbclid: params.get('fbclid'),    // Facebook/Meta click ID
      referrer: document.referrer || null,
      landing_page: window.location.pathname,
      captured_at: new Date().toISOString(),
    };

    // Always capture landing page for attribution analysis
    // Don't overwrite if we already have params (first touch attribution)
    const existing = sessionStorage.getItem(UTM_STORAGE_KEY);
    if (!existing) {
      sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utm));
    }
  } catch {
    // sessionStorage may be blocked in some contexts
    console.warn('Unable to capture UTM params');
  }
}

/**
 * Get stored UTM parameters.
 * Call this on signup to save attribution data.
 */
export function getStoredUTMParams(): UTMParams | null {
  try {
    if (typeof window === 'undefined') return null;

    const stored = sessionStorage.getItem(UTM_STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

/**
 * Clear stored UTM parameters.
 * Call this after successfully saving to database.
 */
export function clearStoredUTMParams(): void {
  try {
    if (typeof window === 'undefined') return;
    sessionStorage.removeItem(UTM_STORAGE_KEY);
  } catch {
    // Ignore errors
  }
}

/**
 * Get attribution source for display.
 * Returns a human-readable source string.
 */
export function getAttributionSource(utm: UTMParams | null): string {
  if (!utm) return 'Direct';

  // Check click IDs first - most reliable attribution
  if (utm.gclid || utm.gbraid || utm.wbraid) return 'Google Ads';
  if (utm.msclkid) return 'Microsoft Ads';
  if (utm.fbclid) return 'Facebook/Meta';

  if (utm.utm_source) {
    const source = utm.utm_source.toLowerCase();
    const medium = utm.utm_medium?.toLowerCase() || '';

    if (source === 'google' && medium === 'cpc') return 'Google Ads';
    if (source === 'google' && medium === 'organic') return 'Google Organic';
    if (source === 'facebook' || source === 'fb') return 'Facebook';
    if (source === 'instagram' || source === 'ig') return 'Instagram';
    if (source === 'linkedin') return 'LinkedIn';
    if (source === 'twitter' || source === 'x') return 'Twitter/X';
    if (source === 'email') return 'Email';
    if (source === 'referral') return 'Referral';

    return utm.utm_source;
  }

  if (utm.referrer) {
    try {
      const url = new URL(utm.referrer);
      const host = url.hostname.replace('www.', '');

      if (host.includes('google')) return 'Google Organic';
      if (host.includes('bing')) return 'Bing Organic';
      if (host.includes('facebook')) return 'Facebook';
      if (host.includes('linkedin')) return 'LinkedIn';
      if (host.includes('twitter') || host.includes('x.com')) return 'Twitter/X';

      return `Referral (${host})`;
    } catch {
      return 'Referral';
    }
  }

  return 'Direct';
}
